<script setup lang="ts" generic="T extends object">
import type { ChartOverlayData } from '#ui-kit/charts/chart-overlay'
import type { StackedBarChartProps } from '#ui-kit/charts/lib/bar'
import { VisAxis, VisStackedBar, VisXYContainer } from '@unovis/vue'
import { useElementSize } from '@vueuse/core'
import { computed, useTemplateRef } from 'vue'
import { ChartOverlay } from '#ui-kit/charts/chart-overlay'
import { ChartTitle } from '#ui-kit/charts/chart-title'
import { getYDomain, makeAxisConfig, makeXAccessors, makeYAccessors } from '#ui-kit/charts/lib/bar'
import { chartColors } from '#ui-kit/charts/lib/colors'
import { getFittedTickStep } from '#ui-kit/charts/lib/ticks'

const props = withDefaults(defineProps<StackedBarChartProps<T>>(), {
  title: '', // avoid explicit opt-out by default
})

const emit = defineEmits<{
  (e: 'hover', data: ChartOverlayData<T>): void
}>()

const slots = defineSlots<{
  overlay?: (props: { d: T }) => void
}>()

// ---------------------------------------------------------------------------------------------------------------------
// accessors
// ---------------------------------------------------------------------------------------------------------------------

const x = computed(() => makeXAccessors(props.x))

const y = computed(() => makeYAccessors(props.data, props.y, props.x))

const yDomain = computed(() => {
  return props.xyContainer?.yDomain ?? getYDomain(props.data, y.value)
})

const chartRoot = useTemplateRef<HTMLElement>('chartRoot')
const { width: chartWidth } = useElementSize(chartRoot)

const tickValues = computed(() => {
  if (!props.xAxis) {
    return undefined
  }

  const { tickValues, tickThreshold } = props.xAxis
  if (tickValues) {
    return tickValues
  }
  if (tickThreshold) {
    const values = props.data?.map((d, i) => x.value(d, i)) ?? []
    const step = getFittedTickStep(Number(tickThreshold), values.length, chartWidth.value)
    return values.filter((d) => Number(d) % step === 0)
  }
  return undefined
})

const color = chartColors.tokenDesigner
</script>

<template>
  <div ref="chartRoot" data-slot="stacked-bar-chart" class="relative w-full">
    <ChartTitle v-if="title" :text="title" />
    <VisXYContainer
      class="chart z-0"
      :data="data"
      v-bind="{
        padding: 5,
        yDomain,
        ...xyContainer,
      }"
    >
      <VisStackedBar
        :color="color"
        :data="data"
        :x="x"
        :y="y"
        v-bind="{
          roundedCorners: false,
          ...stackedBar,
        }"
      />
      <VisAxis
        v-if="xAxis !== false"
        type="x"
        tick-text-color="#43432999"
        v-bind="{
          tickTextWidth: 50,
          tickLine: false,
          gridLine: false,
          tickValues,
          ...makeAxisConfig(xAxis),
        }"
      />
      <VisAxis
        v-if="yAxis !== false"
        type="y"
        tick-text-color="#43432999"
        v-bind="{
          gridLine: true,
          tickLine: true,
          ...makeAxisConfig(yAxis),
        }"
      />
      <!-- @vue-generic {T} -->
      <ChartOverlay
        v-if="slots.overlay"
        :x-axis="typeof props.x === 'string' ? props.x : undefined"
        @hover="emit('hover', $event)"
      >
        <template #default="{ d }">
          <slot name="overlay" :d="d" />
        </template>
      </ChartOverlay>
    </VisXYContainer>
  </div>
</template>

<style scoped>
.unovis-xy-container :deep(svg g.tick line) {
  transform: translateY(0.5px);
}
</style>
